import type { ReactNode } from "react";
import { useSEO } from "@/hooks/use-seo";
import { useLanguage } from "@/contexts/LanguageContext";
import { type LandingEntry, getLandingArticles, formatLandingDate } from "@shared/landing-content";
import { motion, useScroll, useTransform, useMotionValue, useSpring, AnimatePresence } from "framer-motion";
import { Link } from "wouter";
import { ArrowRight, ChevronDown, Play, TrendingUp, Users, Target, Zap, BarChart3, Layers, Brain, Mail, Shield, Eye, Sparkles } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { HeroBackground } from "@/components/HeroBackground";

import amayaLogo from "@assets/images_(2)_1771842328134.png";
import newhouseLogo from "@assets/newhouse-logo-update-03_1771842328135.webp";
import featherWomenLogo from "@assets/feather_women_logo_1771842328135.jpeg";
import frankyLogo from "@assets/franky-logo-bold-zwart-mu3lf19d-2022-07-18-nzrevxra-2022-08-16_1771842328135.png";
import equalsLogo from "@assets/Equals_logo_1771842328135.webp";
import brinckersLogo from "@assets/goed-3_1771842328135.png";
import jutkaRiskaLogo from "@assets/images-3_1771842328135.png";
import mimiEtToiLogo from "@assets/images_1771842328135.png";
import pigHenLogo from "@assets/images_(1)_1771842328135.png";
import moddernLogo from "@assets/Scherm­afbeelding_2026-03-25_om_13.42.34_1774442580194.png";
import zevyLogo from "@assets/zevy_1771843123471.jpg";
import vedderLogo from "@assets/vedder-vedder_1771843123471.jpg";
import mattLogo from "@assets/shopimage_1771843123471.png";

import logoMeta from "@assets/meta_business_partner_1771843057318.webp";
import logoTikTok from "@assets/TikTok-Smart-_1771843057318.webp";
import logoOpenAI from "@assets/fi_ai-029_1771843057318.jpg";

import basImg from "@assets/Scherm\u00ADafbeelding_2026-02-24_om_11.19.40_1771938785535.png";
import juniorImg from "@assets/eIZFVQ4EPdTXUNb3K07jRo1oZVyn0pulPzWwcKV6_(1)_1771928334746.jpg";
import robinImg from "@assets/1678270794100_1771852265016.jpeg";
import shayedImg from "@assets/shayed_sarda_1771928393746.png";

import videoClient1 from "@assets/ad_home_1x1_1774356682562.mp4";
import videoClient2 from "@assets/autumn_1350x1080_1774356682569.mp4";

import dashTopPerformers from "@assets/Scherm\u00ADafbeelding_2026-03-24_om_14.10.52_1774357923590.png";
import dashAdResults from "@assets/Scherm\u00ADafbeelding_2026-03-24_om_14.11.04_1774357923584.png";
import dashAiInsights from "@assets/Scherm\u00ADafbeelding_2026-03-24_om_14.11.16_1774357923591.png";
import dashProcessing from "@assets/Scherm\u00ADafbeelding_2026-03-24_om_14.09.48_1774357923593.png";
import dashCompetitor from "@assets/Scherm\u00ADafbeelding_2026-03-24_om_14.11.46_1774357923593.png";

import clientBoyscouts from "@assets/boyscouts_1774357241226.webp";
import clientJutkaRiska from "@assets/JutkaenRiska_e6868ebf-e03b-43ce-a87c-9d7b4634bf4a_1774357241234.webp";
import clientPigHen from "@assets/pighen_1774357241234.webp";
import clientZevy from "@assets/Zevy-wasparfum-alle-geuren_1774357241235.webp";
import clientAmaya from "@assets/amaya_1774357241235.png";
import clientArtPic from "@assets/artPic_public_L_3495432_1774357241235.webp";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const sectionIcons = [Target, BarChart3, Layers, Zap, Brain, Mail, Shield, Eye, Sparkles, TrendingUp, Users];

export interface LandingPageData {
  seo: {
    nl: { title: string; description: string; keywords: string };
    en: { title: string; description: string; keywords: string };
  };
  path: string;
  /** Canonieke content uit shared/landing-content.ts — één bron voor SSR én client. */
  content: LandingEntry;
  stats?: { nl: { value: string; label: string }[]; en: { value: string; label: string }[] };
  /** Optionele extra secties (ReactNode), gerenderd direct na de hero — bijv. de Signal-showcase. */
  extra?: ReactNode;
  diagram?: string;
}

const defaultStats = {
  nl: [
    { value: "100+", label: "merken geholpen" },
    { value: "€10M+", label: "Ad spend beheerd" },
    { value: "3.2x", label: "Gemiddelde ROAS" },
    { value: "24/7", label: "AI-monitoring" },
  ],
  en: [
    { value: "100+", label: "brands helped" },
    { value: "€10M+", label: "Ad spend managed" },
    { value: "3.2x", label: "Average ROAS" },
    { value: "24/7", label: "AI monitoring" },
  ],
};

const clientLogos = [
  { src: zevyLogo, alt: "Zèvy" },
  { src: vedderLogo, alt: "Vedder & Vedder" },
  { src: mattLogo, alt: "Matt Sleeps" },
  { src: amayaLogo, alt: "Amaya" },
  { src: newhouseLogo, alt: "Newhouse" },
  { src: featherWomenLogo, alt: "Feather Women" },
  { src: frankyLogo, alt: "Franky Amsterdam" },
  { src: equalsLogo, alt: "Equals" },
  { src: brinckersLogo, alt: "Brinckers" },
  { src: jutkaRiskaLogo, alt: "Jutka & Riska" },
  { src: mimiEtToiLogo, alt: "Mimi et Toi" },
  { src: pigHenLogo, alt: "Pig & Hen" },
  { src: moddernLogo, alt: "Moddern" },
];

const teamMembers = [
  { name: "Bas Lens", role: { nl: "Founder", en: "Founder" }, img: basImg },
  { name: "Shayed Sarda", role: { nl: "Head of Digital", en: "Head of Digital" }, img: shayedImg },
  { name: "Robin de Wildt", role: { nl: "Digital Growth Expert", en: "Digital Growth Expert" }, img: robinImg },
  { name: "Junior Panders", role: { nl: "Development Director", en: "Development Director" }, img: juniorImg },
];

const partnerLogos = [
  { src: logoMeta, alt: "Meta Business Partner" },
  { src: logoTikTok, alt: "TikTok Marketing Partner" },
  { src: logoOpenAI, alt: "OpenAI" },
];

function FAQItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      {...fadeUp}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="border-b border-white/10"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group"
        data-testid={`faq-toggle-${index}`}
      >
        <span className="text-base sm:text-lg font-display font-semibold text-white/90 pr-4 group-hover:text-[#4CEAAC] transition-colors">{q}</span>
        <ChevronDown className={`w-5 h-5 text-white/40 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-[500px] pb-5" : "max-h-0"}`}>
        <p className="text-white/60 leading-relaxed text-[15px]">{a}</p>
      </div>
    </motion.div>
  );
}

function GrowthCompassDiagram({ nl }: { nl: boolean }) {
  const phases = nl
    ? [
        { icon: Eye, label: "Intelligence", desc: "Data & analyse" },
        { icon: Sparkles, label: "Creation", desc: "Creative & content" },
        { icon: Target, label: "Execution", desc: "Campagnes & optimalisatie" },
        { icon: Mail, label: "Retention", desc: "E-mail & lifecycle" },
      ]
    : [
        { icon: Eye, label: "Intelligence", desc: "Data & analysis" },
        { icon: Sparkles, label: "Creation", desc: "Creative & content" },
        { icon: Target, label: "Execution", desc: "Campaigns & optimization" },
        { icon: Mail, label: "Retention", desc: "Email & lifecycle" },
      ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="my-16"
    >
      <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-[#256C43]/10 to-[#0A1F14] p-8 sm:p-12 overflow-hidden" style={{ animation: 'pulse-glow 10s ease-in-out infinite' }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[400px] md:h-[400px] pointer-events-none opacity-10">
          <div className="w-full h-full rounded-full border border-dashed border-[#4CEAAC]/30" style={{ animation: 'glow-ring 40s linear infinite' }} />
        </div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] md:w-[280px] md:h-[280px] pointer-events-none opacity-10">
          <div className="w-full h-full rounded-full border border-[#4CEAAC]/20" style={{ animation: 'glow-ring 30s linear infinite reverse' }} />
        </div>
        <div className="absolute top-4 right-6 text-xs text-white/20 uppercase tracking-widest font-semibold">Growth Compass</div>
        <h3 className="text-xl sm:text-2xl font-display font-bold mb-8 text-white/90 relative z-10">
          {nl ? "Het Growth Compass Framework" : "The Growth Compass Framework"}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 relative z-10">
          {phases.map((phase, i) => {
            const Icon = phase.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.8, rotateY: 30 }}
                whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.12, type: "spring" }}
                className="relative group"
              >
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 sm:p-6 text-center hover:border-[#4CEAAC]/30 hover:bg-[#4CEAAC]/[0.03] transition-all duration-300 backdrop-blur-sm">
                  <motion.div
                    className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#256C43]/30 flex items-center justify-center group-hover:bg-[#4CEAAC]/20 transition-colors relative"
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 2 + i * 0.5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Icon className="w-6 h-6 text-[#4CEAAC]" />
                  </motion.div>
                  <div className="text-sm font-display font-bold text-white/90 mb-1">{phase.label}</div>
                  <div className="text-xs text-white/50">{phase.desc}</div>
                </div>
                {i < phases.length - 1 && (
                  <motion.div
                    className="hidden md:block absolute top-1/2 -right-3 sm:-right-3 h-[2px] bg-[#4CEAAC]/20"
                    initial={{ width: 0 }}
                    whileInView={{ width: 16 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.12 + 0.5 }}
                  />
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

function ClientVideos({ nl }: { nl: boolean }) {
  return (
    <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="my-12">
      <div className="mb-4">
        <p className="text-xs font-semibold text-[#4CEAAC]/40 uppercase tracking-widest mb-1">
          {nl ? "Klantresultaten" : "Client results"}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative rounded-2xl overflow-hidden border border-white/10 hover:border-[#4CEAAC]/20 transition-all duration-300 aspect-square">
          <video
            src={videoClient1}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F14]/60 via-transparent to-transparent pointer-events-none" />
        </div>
        <div className="relative rounded-2xl overflow-hidden border border-white/10 hover:border-[#4CEAAC]/20 transition-all duration-300 aspect-square">
          <video
            src={videoClient2}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F14]/60 via-transparent to-transparent pointer-events-none" />
        </div>
      </div>
    </motion.div>
  );
}

const clientImages = [
  { src: clientBoyscouts, alt: "Boyscouts of Amsterdam — product fotografie" },
  { src: clientJutkaRiska, alt: "Jutka & Riska — fashion campagne" },
  { src: clientPigHen, alt: "Pig&Hen — product fotografie" },
  { src: clientZevy, alt: "Zévy — product lifestyle" },
  { src: clientAmaya, alt: "Amaya Amsterdam — fashion campagne" },
  { src: clientArtPic, alt: "Babboe — lifestyle campagne" },
];

function ClientImageStrip({ indices, layout = "trio" }: { indices: number[]; layout?: "trio" | "duo" | "hero" }) {
  const images = indices.map(i => clientImages[i % clientImages.length]);

  if (layout === "hero") {
    return (
      <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="my-14">
        <div className="relative rounded-2xl overflow-hidden aspect-[21/9] border border-white/10">
          <img
            src={images[0].src}
            alt={images[0].alt}
            loading="lazy"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F14]/70 via-[#0A1F14]/20 to-transparent pointer-events-none" />
        </div>
      </motion.div>
    );
  }

  if (layout === "duo") {
    return (
      <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="my-14">
        <div className="grid grid-cols-2 gap-4">
          {images.slice(0, 2).map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: i === 0 ? -20 : 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="relative rounded-xl overflow-hidden aspect-[4/3] border border-white/10 group"
            >
              <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F14]/50 via-transparent to-transparent pointer-events-none" />
            </motion.div>
          ))}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="my-14">
      <div className="grid grid-cols-3 gap-3 md:gap-4">
        {images.slice(0, 3).map((img, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.08 }}
            className="relative rounded-xl overflow-hidden aspect-[4/5] border border-white/10 hover:border-[#4CEAAC]/15 transition-all duration-300 group"
          >
            <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F14]/50 via-transparent to-transparent pointer-events-none" />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

const allDashboardScreenshots = [
  { src: dashCompetitor, alt: "wrkt signal — Competitor analyse" },
  { src: dashProcessing, alt: "wrkt signal — AI Analyse" },
  { src: dashTopPerformers, alt: "wrkt signal — Top performers" },
  { src: dashAiInsights, alt: "wrkt signal — AI aanbevelingen" },
  { src: dashAdResults, alt: "wrkt signal — Ad resultaten" },
];

function DashboardProof({ indices, nl }: { indices: number[]; nl: boolean }) {
  const shots = indices.map(i => allDashboardScreenshots[i % allDashboardScreenshots.length]);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="my-10"
    >
      <p className="text-[10px] font-semibold text-[#4CEAAC]/30 uppercase tracking-widest mb-3">
        {nl ? "wrkt signal" : "wrkt signal"}
      </p>
      <div className={`grid gap-3 ${shots.length === 1 ? 'grid-cols-1 max-w-md' : shots.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
        {shots.map((shot, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.08, type: "spring" }}
            className="relative rounded-lg overflow-hidden border border-white/10 hover:border-[#4CEAAC]/20 transition-all duration-300 group aspect-[16/10]"
          >
            <img
              src={shot.src}
              alt={shot.alt}
              loading="lazy"
              className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F14]/40 via-transparent to-transparent pointer-events-none" />
            <div
              className="absolute left-0 w-full h-[1px] pointer-events-none opacity-0 group-hover:opacity-100"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(76,234,172,0.5), transparent)',
                animation: 'scan-line 6s ease-in-out infinite',
              }}
            />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

export default function LandingPage({ data }: { data: LandingPageData }) {
  const { language, localePath } = useLanguage();
  const nl = language === "nl";
  const lang = nl ? "nl" : "en";

  useSEO({
    title: data.seo[lang].title,
    description: data.seo[lang].description,
    path: data.path,
    lang,
    keywords: data.seo[lang].keywords,
  });

  const content = data.content[lang];
  const sections = content.sections;
  const pricing = content.pricing;
  const facts = content.facts;
  const faq = content.faq;
  const cta = content.cta;
  const links = content.links;
  const stats = data.stats ? data.stats[lang] : defaultStats[lang];
  const furtherReading = getLandingArticles(data.path, lang);

  return (
    <div className="min-h-screen bg-[#0A1F14] text-white font-sans selection:bg-[#4CEAAC] selection:text-[#0A1F14] relative">
      <style>{`
        @keyframes color-pulse-shift {
          0% { background-color: rgba(141, 24, 43, 0.5); transform: scale(1); opacity: 0.5; }
          50% { background-color: rgba(76, 234, 172, 0.2); transform: scale(1.04); opacity: 0.35; }
          100% { background-color: rgba(141, 24, 43, 0.5); transform: scale(1); opacity: 0.5; }
        }
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes float-orbit {
          0% { transform: rotate(0deg) translateX(140px) rotate(0deg); }
          100% { transform: rotate(360deg) translateX(140px) rotate(-360deg); }
        }
        @keyframes float-orbit-reverse {
          0% { transform: rotate(0deg) translateX(200px) rotate(0deg); }
          100% { transform: rotate(-360deg) translateX(200px) rotate(360deg); }
        }
        @keyframes scan-line {
          0% { top: -4px; opacity: 0; }
          10% { opacity: 0.6; }
          90% { opacity: 0.6; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes glow-ring {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 15px rgba(76, 234, 172, 0.05); }
          50% { box-shadow: 0 0 25px rgba(76, 234, 172, 0.12); }
        }
        @keyframes border-flow {
          0% { background-position: 0% 0%; }
          100% { background-position: 200% 0%; }
        }
      `}</style>

      <div className="relative z-10">
      <Navbar />


      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        <HeroBackground />
        {[...Array(5)].map((_, i) => (
          <div
            key={`particle-${i}`}
            className="absolute pointer-events-none z-0"
            style={{
              top: `${20 + i * 15}%`,
              left: `${50 + (i % 2 === 0 ? 10 : -10)}%`,
              width: `${4 + i * 2}px`,
              height: `${4 + i * 2}px`,
              borderRadius: '50%',
              background: i % 2 === 0 ? 'rgba(76,234,172,0.3)' : 'rgba(141,24,43,0.4)',
              animation: `${i % 2 === 0 ? 'float-orbit' : 'float-orbit-reverse'} ${30 + i * 8}s linear infinite`,
            }}
          />
        ))}
        <div className="container mx-auto px-6 md:px-12 relative z-10 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="pill pill--brand pill--dot pill--dot-pulse pill--lg mb-8 uppercase tracking-wider"
          >
            <span>{nl ? "AI-First Growth Agency" : "AI-First Growth Agency"}</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.4rem] font-display font-bold tracking-tight leading-[1.12] mb-6"
            data-testid="text-landing-h1"
          >
            {content.h1}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-lg sm:text-xl text-white/70 leading-relaxed mb-6 font-display max-w-3xl"
          >
            {content.intro}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="text-base text-white/50 leading-relaxed mb-10 max-w-2xl"
          >
            {content.introParagraph}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap gap-4"
          >
            <Link href={localePath("/scan")} className="btn btn--gradient btn--lg" data-testid="button-landing-hero-cta">
              {nl ? "Start je gratis Growth Scan" : "Start your free Growth Scan"} <ArrowRight className="w-5 h-5" />
            </Link>
            <Link href={localePath("/cases")} className="btn btn--outline btn--lg" data-testid="button-landing-hero-cases">
              {nl ? "Bekijk onze cases" : "View our cases"}
            </Link>
          </motion.div>
        </div>
      </section>

      {data.extra}

      {/* Client Videos — visual impact */}
      <section className="py-10 border-b border-white/5">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl">
          <ClientVideos nl={nl} />
          <ClientImageStrip indices={[0, 1, 2]} layout="trio" />
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-14 border-y border-white/5 bg-black/30 backdrop-blur-sm relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(76,234,172,0.04) 0%, transparent 70%)' }} />
        <div className="container mx-auto px-6 md:px-12 max-w-5xl relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12, type: "spring", stiffness: 200 }}
                className="text-center relative"
                data-testid={`stat-${i}`}
              >
                <motion.div
                  className="text-3xl sm:text-4xl font-display font-bold text-[#4CEAAC] mb-1"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.12 + 0.2 }}
                >
                  {stat.value}
                </motion.div>
                <div className="text-sm text-white/40">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Logo Bar */}
      <section className="py-10 overflow-hidden border-b border-white/5 bg-black">
        <div className="container mx-auto px-6 mb-5 text-center">
          <p className="text-xs font-semibold text-white/30 uppercase tracking-widest">
            {nl ? "Vertrouwd door 100+ merken" : "Trusted by 100+ brands"}
          </p>
        </div>
        <div className="w-full overflow-hidden flex flex-col justify-center py-4">
          <div className="flex w-fit hover:[animation-play-state:paused]" style={{ animation: 'marquee 30s linear infinite' }}>
            {[...Array(2)].map((_, arrayIndex) => (
              <div key={arrayIndex} className="flex items-center justify-center gap-10 md:gap-20 px-5 md:px-10">
                {clientLogos.map((brand, i) => (
                  <div
                    key={`${arrayIndex}-${i}`}
                    className="flex-shrink-0 h-12 md:h-16 px-4 py-3 flex items-center justify-center opacity-50 hover:opacity-90 transition-all duration-300"
                  >
                    <img
                      src={brand.src}
                      alt={brand.alt}
                      loading="lazy"
                      decoding="async"
                      style={{ filter: 'grayscale(1) invert(1) contrast(1.25)', mixBlendMode: 'screen' }}
                      className="max-h-full max-w-[100px] md:max-w-[130px] object-contain"
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content Sections */}
      <div className="container mx-auto px-6 md:px-12 max-w-5xl pb-10 pt-16">

        {sections.map((section, si) => {
          const Icon = sectionIcons[si % sectionIcons.length];
          const hasSubsections = section.subsections && section.subsections.length > 0;

          return (
            <motion.section
              key={si}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: si * 0.05 }}
              className="mb-20"
            >
              <div className="flex items-start gap-4 mb-6">
                <motion.div
                  className="w-10 h-10 rounded-lg bg-[#256C43]/20 flex items-center justify-center shrink-0 mt-1 border border-[#4CEAAC]/10"
                  whileInView={{ rotate: [0, -10, 10, 0] }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: si * 0.05 + 0.3 }}
                  style={{ animation: 'pulse-glow 10s ease-in-out infinite' }}
                >
                  <Icon className="w-5 h-5 text-[#4CEAAC]/70" />
                </motion.div>
                <motion.h2
                  className="text-2xl sm:text-3xl font-display font-bold text-white/95"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: si * 0.05 + 0.15 }}
                >
                  {section.title}
                </motion.h2>
              </div>

              {section.content.length > 0 && (
                <div className={hasSubsections ? "mb-8" : ""}>
                  {section.content.map((p, pi) => (
                    <motion.p
                      key={pi}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: pi * 0.08 }}
                      className="text-white/60 leading-relaxed mb-4 text-[15px] sm:text-base ml-14"
                    >
                      {p}
                    </motion.p>
                  ))}
                </div>
              )}

              {hasSubsections && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-0 md:ml-14">
                  {section.subsections!.map((sub, subi) => (
                    <motion.div
                      key={subi}
                      initial={{ opacity: 0, y: 20, scale: 0.97 }}
                      whileInView={{ opacity: 1, y: 0, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: subi * 0.08, type: "spring", stiffness: 150 }}
                      className="relative rounded-xl p-[1px] overflow-hidden group"
                    >
                      <div
                        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        style={{
                          background: 'linear-gradient(90deg, transparent, rgba(76,234,172,0.3), transparent)',
                          backgroundSize: '200% 100%',
                          animation: 'border-flow 8s linear infinite',
                        }}
                      />
                      <div className="absolute inset-0 rounded-xl border border-white/[0.07] group-hover:border-transparent transition-all duration-300" />
                      <div className="relative rounded-xl bg-[#0A1F14]/80 p-5 sm:p-6 backdrop-blur-sm">
                        <h3 className="text-base sm:text-lg font-display font-semibold mb-3 text-white/85 group-hover:text-[#4CEAAC]/90 transition-colors">{sub.title}</h3>
                        {sub.content.map((p, pi) => (
                          <p key={pi} className="text-white/50 leading-relaxed text-sm">{p}</p>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {si === 1 && <DashboardProof indices={[0, 1]} nl={nl} />}
              {si === 1 && <ClientImageStrip indices={[3, 4]} layout="duo" />}
              {si === 2 && <DashboardProof indices={[2]} nl={nl} />}
              {si === 3 && <ClientImageStrip indices={[5]} layout="hero" />}
              {si === 3 && <DashboardProof indices={[3, 4]} nl={nl} />}
            </motion.section>
          );
        })}

        {/* Prijssectie */}
        <motion.section {...fadeUp} transition={{ duration: 0.5 }} className="mb-20">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#256C43]/20 flex items-center justify-center shrink-0 mt-1 border border-[#4CEAAC]/10">
              <BarChart3 className="w-5 h-5 text-[#4CEAAC]/70" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-white/95">{pricing.title}</h2>
          </div>
          {pricing.content.map((p, pi) => (
            <p key={pi} className="text-white/60 leading-relaxed mb-4 text-[15px] sm:text-base ml-14">
              {p}
            </p>
          ))}
        </motion.section>

        {/* Feiten & cijfers */}
        <motion.section {...fadeUp} transition={{ duration: 0.5 }} className="mb-20">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 sm:p-10">
            <h2 className="text-xl sm:text-2xl font-display font-bold text-white/95 mb-6">{facts.title}</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {facts.items.map((item, i) => (
                <li key={i} className="text-white/60 text-[15px] flex items-start gap-3" data-testid={`fact-${i}`}>
                  <span className="text-[#4CEAAC] leading-relaxed">•</span>
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.section>

        {/* Growth Compass Diagram */}
        <GrowthCompassDiagram nl={nl} />

        {/* Team Section */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="my-20"
        >
          <div className="text-center mb-10">
            <motion.p
              className="text-xs font-semibold text-[#4CEAAC]/50 uppercase tracking-widest mb-3"
              initial={{ opacity: 0, letterSpacing: '0em' }}
              whileInView={{ opacity: 1, letterSpacing: '0.15em' }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {nl ? "Jouw team" : "Your team"}
            </motion.p>
            <h2 className="text-2xl sm:text-3xl font-display font-bold">
              {nl ? "De mensen achter de resultaten" : "The people behind the results"}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {teamMembers.map((member, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30, rotateY: 15 }}
                whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1, type: "spring" }}
                className="text-center group"
              >
                <div className="relative rounded-xl overflow-hidden aspect-square mb-3 border border-white/10 group-hover:border-[#4CEAAC]/20 transition-all duration-300">
                  <img
                    src={member.img}
                    alt={member.name}
                    loading="lazy"
                    style={{ filter: 'grayscale(1)', transition: 'filter 0.5s ease' }}
                    className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                    onMouseEnter={(e) => { (e.target as HTMLImageElement).style.filter = 'grayscale(0)'; }}
                    onMouseLeave={(e) => { (e.target as HTMLImageElement).style.filter = 'grayscale(1)'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F14]/60 to-transparent pointer-events-none" />
                </div>
                <div className="text-sm font-display font-semibold text-white/80">{member.name}</div>
                <div className="text-xs text-white/40">{member.role[lang]}</div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* FAQ */}
        {faq.length > 0 && (
          <motion.section {...fadeUp} transition={{ duration: 0.5 }} className="mb-20">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-display font-bold mb-8 text-center">{nl ? "Veelgestelde vragen" : "Frequently asked questions"}</h2>
              <div className="border-t border-white/10">
                {faq.map((f, i) => (
                  <FAQItem key={i} q={f.q} a={f.a} index={i} />
                ))}
              </div>
            </div>
          </motion.section>
        )}

        {/* Verder lezen */}
        {furtherReading.length > 0 && (
          <motion.section {...fadeUp} transition={{ duration: 0.5 }} className="mb-20">
            <h2 className="text-2xl sm:text-3xl font-display font-bold mb-8 text-center">{content.furtherReadingTitle}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {furtherReading.map((a) => (
                <Link
                  key={a.slug}
                  href={localePath(`/artikelen/${a.slug}`)}
                  className="rounded-xl border border-white/10 bg-white/[0.02] p-5 sm:p-6 hover:border-[#4CEAAC]/30 transition-colors group flex items-start"
                  data-testid={`link-further-reading-${a.slug}`}
                >
                  <span className="text-sm sm:text-base font-display font-semibold text-white/80 group-hover:text-[#4CEAAC] transition-colors leading-snug">
                    {a.title}
                  </span>
                </Link>
              ))}
            </div>
          </motion.section>
        )}

        {/* CTA Block */}
        <motion.section
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, type: "spring" }}
          className="my-20"
        >
          <div className="relative rounded-2xl p-[1px] overflow-hidden" style={{ animation: 'pulse-glow 12s ease-in-out infinite' }}>
            <div
              className="absolute inset-0 rounded-2xl"
              style={{
                background: 'linear-gradient(90deg, rgba(76,234,172,0.1), rgba(76,234,172,0.25), rgba(141,24,43,0.15), rgba(76,234,172,0.1))',
                backgroundSize: '200% 100%',
                animation: 'border-flow 10s linear infinite',
              }}
            />
            <div className="relative rounded-2xl bg-gradient-to-br from-[#256C43]/20 to-[#0A1F14] overflow-hidden">
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-1/2 -right-1/2 w-[500px] h-[500px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, rgba(76,234,172,0.3), transparent 60%)', animation: 'float-orbit 35s linear infinite' }} />
              </div>
              <div className="relative p-8 sm:p-12 md:p-16 flex flex-col md:flex-row items-start md:items-center gap-8">
                <div className="flex-1">
                  <motion.h2
                    className="text-2xl sm:text-3xl md:text-4xl font-display font-bold mb-4"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    {cta.title}
                  </motion.h2>
                  <motion.p
                    className="text-white/60 leading-relaxed max-w-xl text-[15px] sm:text-base"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    {cta.text}
                  </motion.p>
                </div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.4, type: "spring" }}
                >
                  <Link href={localePath("/scan")} className="btn btn--gradient btn--lg" data-testid="button-landing-cta">
                    {nl ? "Start je gratis Growth Scan" : "Start your free Growth Scan"} <ArrowRight className="w-5 h-5" />
                  </Link>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Partner Badges */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <div className="text-center mb-8">
            <motion.p
              className="text-xs font-semibold text-white/25 uppercase tracking-widest"
              initial={{ opacity: 0, letterSpacing: '0em' }}
              whileInView={{ opacity: 1, letterSpacing: '0.15em' }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {nl ? "Gecertificeerd partner" : "Certified partner"}
            </motion.p>
          </div>
          <div className="flex items-center justify-center gap-8 md:gap-16">
            {partnerLogos.map((partner, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                whileInView={{ opacity: 0.4, scale: 1, y: 0 }}
                whileHover={{ opacity: 0.8, scale: 1.05 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15, type: "spring" }}
                className="h-12 md:h-16 flex items-center justify-center transition-all duration-300"
              >
                <img
                  src={partner.src}
                  alt={partner.alt}
                  loading="lazy"
                  style={{ filter: 'grayscale(1) invert(1) contrast(1.25)', mixBlendMode: 'screen' }}
                  className="max-h-full max-w-[100px] md:max-w-[140px] object-contain"
                />
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Internal Links */}
        <motion.nav {...fadeUp} transition={{ duration: 0.4 }} className="mb-16 pt-8 border-t border-white/10">
          <p className="text-xs text-white/30 uppercase tracking-widest font-semibold mb-4">{nl ? "Meer lezen" : "Read more"}</p>
          <div className="flex flex-wrap gap-3">
            {links.map((l, i) => (
              <Link
                key={i}
                href={localePath(l.href)}
                className="text-sm text-white/50 hover:text-[#4CEAAC] transition-colors"
                data-testid={`link-related-${i}`}
              >
                {l.label}{i < links.length - 1 ? " ·" : ""}
              </Link>
            ))}
          </div>
        </motion.nav>

        {/* Laatst bijgewerkt */}
        <p className="text-xs text-white/30 mb-16" data-testid="text-last-updated">
          {nl ? "Laatst bijgewerkt" : "Last updated"}: {formatLandingDate(data.content.lastModified, lang)}
        </p>
      </div>

      <Footer />
      </div>
    </div>
  );
}
